/**
 * StepDetail — Expanded view for editing a single step.
 * Shows: type chooser, type-specific fields, failure handling, action buttons (move/run/delete).
 * Matches the mockup's expanded step panel exactly.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, FormControl, Select, MenuItem, Checkbox,
  FormControlLabel, IconButton, Tooltip, Button, Divider, Autocomplete,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, Chip, Stack,
} from '@mui/material';
import extensionScriptsService from '../../services/api/extensionScriptsService';
import {
  ArrowUpward as UpIcon, ArrowDownward as DownIcon, PlayArrow as PlayIcon,
  Delete as DeleteIcon, ContentCopy as CopyIcon,
  MyLocation as PickerIcon,
} from '@mui/icons-material';
import {
  STEP_TYPE_ITEMS, STEP_TEMPLATES, FAILURE_OPTIONS, ASSERTION_OPERATORS,
  getStepTypeKey,
} from './stepTypes';

// Suggest a variable name from the field label and current value.
const suggestVariableName = (label, value) => {
  const l = String(label || '').toLowerCase();
  const v = String(value || '');
  if (/password/.test(l)) return 'password';
  if (/user|login|identifier/.test(l)) return 'username';
  if (/email/.test(l) || /@/.test(v)) return 'email';
  if (/url|link|href/.test(l) || /^https?:\/\//.test(v)) return 'url';
  if (/first.*name/.test(l)) return 'firstName';
  if (/last.*name/.test(l)) return 'lastName';
  if (/name/.test(l)) return 'name';
  return 'value';
};

// Button + dialog that extracts the current literal value into a {{variable}}.
// Relies on the VariablesPanel auto-detecting {{name}} references in step JSON —
// no extra wiring needed; the new variable appears in the panel on next render.
const ExtractVariableButton = ({ value, onChange, label = 'Value' }) => {
  const [open, setOpen] = useState(false);
  const [varName, setVarName] = useState('');
  const literal = String(value || '');
  const isAlreadyVar = /^\{\{[^}]+\}\}$/.test(literal);

  const openDialog = () => {
    setVarName(suggestVariableName(label, literal));
    setOpen(true);
  };

  const confirm = () => {
    const clean = (varName || '').trim().replace(/[^a-zA-Z0-9_]/g, '');
    if (!clean) return;
    onChange(`{{${clean}}}`);
    setOpen(false);
  };

  return (
    <>
      <Tooltip title={isAlreadyVar ? 'Already a variable' : 'Extract this value to a variable'}>
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={!literal || isAlreadyVar}
            sx={{ minWidth: 0, px: 0.8, fontSize: '0.7rem', whiteSpace: 'nowrap' }}
            onClick={openDialog}
          >
            {'{{…}}'}
          </Button>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Extract to variable</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The current value <code style={{ fontFamily: 'monospace' }}>{literal.length > 40 ? literal.substring(0, 40) + '…' : literal}</code> will be replaced with <code>{`{{${varName || 'varName'}}}`}</code>.
            The original value becomes the variable's default — edit it in the Variables panel.
          </Typography>
          <TextField
            size="small"
            fullWidth
            autoFocus
            label="Variable name"
            value={varName}
            onChange={(e) => setVarName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }}
            helperText="Letters, digits, and underscore only"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirm} disabled={!varName.trim()}>Extract</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ---- Shared field components ----

/**
 * Selector input. When `onPick` is passed (extension sidebar only), renders
 * a crosshair IconButton that activates the element picker and writes the
 * picked selector back via onChange. See issue #68.
 */
const SelectorField = ({ value, onChange, onPick }) => {
  const [picking, setPicking] = useState(false);
  const handlePick = () => {
    if (!onPick || picking) return;
    setPicking(true);
    onPick((payload) => {
      // payload is invoked twice in the happy path: once with the local
      // selector, once with the AI-upgraded selector. Both invocations just
      // overwrite the field — the final value is whichever arrived last.
      if (payload && payload.selector) onChange(payload.selector);
      if (payload && payload.upgraded) setPicking(false);
    });
    // Safety timeout — in case no upgraded payload ever arrives (LLM offline),
    // unstick the spinner after 8s so the user can click again.
    setTimeout(() => setPicking(false), 8000);
  };
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Selector
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <TextField
          size="small"
          fullWidth
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          variant="outlined"
          placeholder="e.g. #login-input, button.submit"
          sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
        />
        {onPick && (
          <Tooltip title={picking ? 'Click an element on the page…' : 'Pick element on page (updates this selector)'}>
            <span>
              <IconButton
                size="small"
                onClick={handlePick}
                disabled={picking}
                data-automatrix-testid="step-selector-picker"
                sx={{
                  border: '1px solid',
                  borderColor: picking ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  bgcolor: picking ? 'primary.light' : 'transparent',
                }}
              >
                {picking ? <CircularProgress size={14} /> : <PickerIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const ValueFieldWithCred = ({ value, onChange, label = 'Value' }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <TextField
        size="small"
        fullWidth
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        variant="outlined"
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
      <Tooltip title="Insert {{credential.username}}">
        <Button
          size="small"
          variant="outlined"
          sx={{ minWidth: 0, px: 0.8, fontSize: '0.7rem', whiteSpace: 'nowrap' }}
          onClick={() => onChange('{{credential.username}}')}
        >
          {'{{user}}'}
        </Button>
      </Tooltip>
      <Tooltip title="Insert {{credential.password}}">
        <Button
          size="small"
          variant="outlined"
          sx={{ minWidth: 0, px: 0.8, fontSize: '0.7rem', whiteSpace: 'nowrap' }}
          onClick={() => onChange('{{credential.password}}')}
        >
          {'{{pass}}'}
        </Button>
      </Tooltip>
      <ExtractVariableButton value={value} onChange={onChange} label={label} />
    </Box>
  </Box>
);

const TimeoutField = ({ value, onChange }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
      Timeout (ms)
    </Typography>
    <TextField
      size="small"
      value={value || ''}
      type="number"
      onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
      variant="outlined"
      sx={{ width: 120, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
    />
  </Box>
);

const FailureHandlingField = ({ value, onChange }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
      On Failure
    </Typography>
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <Select
        value={value || 'stop'}
        onChange={(e) => onChange(e.target.value)}
        variant="outlined"
        sx={{ fontSize: '0.8rem' }}
      >
        {FAILURE_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
);

const NameField = ({ value, onChange }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
      Name
    </Typography>
    <TextField
      size="small"
      fullWidth
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      variant="outlined"
      sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
    />
  </Box>
);

// ---- Type-specific field renderers ----

const StepTypeFields = ({ step, onFieldChange, onPickSelector }) => {
  const d = step.inputData || {};

  // Helper: get field value from inputData first, then top-level step
  const getField = (field) => d[field] !== undefined ? d[field] : step[field];

  const updateInput = (field, val) => {
    onFieldChange({ ...step, [field]: val, inputData: { ...d, [field]: val } });
  };

  switch (step.type) {
    case 'action':
      switch (d.action) {
        case 'navigate':
          return (
            <>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>URL</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <TextField
                    size="small" fullWidth value={step.url || d.url || ''} variant="outlined"
                    onChange={(e) => {
                      updateInput('url', e.target.value);
                      onFieldChange({ ...step, url: e.target.value, inputData: { ...d, url: e.target.value } });
                    }}
                    placeholder="https://www.khanacademy.org/login"
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                  />
                  <ExtractVariableButton
                    value={step.url || d.url || ''}
                    onChange={(v) => {
                      updateInput('url', v);
                      onFieldChange({ ...step, url: v, inputData: { ...d, url: v } });
                    }}
                    label="URL"
                  />
                </Box>
              </Box>
              <TimeoutField value={getField('timeout') || step.timeout || ''} onChange={(v) => updateInput('timeout', v)} />
              <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
            </>
          );
        case 'type':
          return (
            <>
              <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
              <SelectorField value={getField('selector')} onChange={(v) => updateInput('selector', v)} onPick={onPickSelector} />
              <ValueFieldWithCred value={getField('text')} onChange={(v) => updateInput('text', v)} label="Text / Value" />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={getField('clearFirst') !== false}
                    onChange={(e) => updateInput('clearFirst', e.target.checked)}
                  />
                }
                label={<Typography variant="caption">Clear field first</Typography>}
                sx={{ mb: 1 }}
              />
              <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />
              <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
            </>
          );
        case 'click':
          return (
            <>
              <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
              <SelectorField value={getField('selector')} onChange={(v) => updateInput('selector', v)} onPick={onPickSelector} />
              <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />
              <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
            </>
          );
        case 'select':
          return (
            <>
              <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
              <SelectorField value={getField('selector')} onChange={(v) => updateInput('selector', v)} onPick={onPickSelector} />
              <ValueFieldWithCred value={getField('value')} onChange={(v) => updateInput('value', v)} label="Option Value" />
              <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />
              <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
            </>
          );
        case 'script':
          return (
            <>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Script Body</Typography>
                <TextField
                  size="small" fullWidth multiline rows={4} value={d.script || ''} variant="outlined"
                  onChange={(e) => updateInput('script', e.target.value)}
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Box>
              <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />
              <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
            </>
          );
        default:
          return (
            <>
              {d.selector && <SelectorField value={getField('selector')} onChange={(v) => updateInput('selector', v)} onPick={onPickSelector} />}
              <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />
            </>
          );
      }

    case 'wait':
      switch (d.waitType) {
        case 'selector':
          return (
            <>
              <SelectorField value={getField('selector')} onChange={(v) => updateInput('selector', v)} onPick={onPickSelector} />
              <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />
              <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
            </>
          );
        case 'navigation':
          return (
            <>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Expected URL (optional)</Typography>
                <TextField
                  size="small" fullWidth value={getField('url') || ''} variant="outlined"
                  onChange={(e) => updateInput('url', e.target.value)}
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Box>
              <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />
              <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
            </>
          );
        case 'time':
          return (
            <>
              <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />
              <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
            </>
          );
        default:
          return <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />;
      }

    case 'assertion':
      switch (d.assertionType) {
        case 'url':
          return (
            <>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Operator</Typography>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <Select
                    value={getField('operator') || 'contains'} variant="outlined" sx={{ fontSize: '0.8rem' }}
                    onChange={(e) => updateInput('operator', e.target.value)}
                  >
                    {ASSERTION_OPERATORS.map((op) => (
                      <MenuItem key={op.value} value={op.value} sx={{ fontSize: '0.8rem' }}>{op.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Expected URL / Value</Typography>
                <TextField
                  size="small" fullWidth value={getField('value') || ''} variant="outlined"
                  onChange={(e) => updateInput('value', e.target.value)}
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Box>
              <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />
              <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
            </>
          );
        case 'element':
          return (
            <>
              <SelectorField value={getField('selector')} onChange={(v) => updateInput('selector', v)} onPick={onPickSelector} />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={d.exists !== false}
                    onChange={(e) => updateInput('exists', e.target.checked)}
                  />
                }
                label={<Typography variant="caption">Element should exist</Typography>}
                sx={{ mb: 1 }}
              />
              <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />
              <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
            </>
          );
        case 'text':
          return (
            <>
              <SelectorField value={getField('selector')} onChange={(v) => updateInput('selector', v)} onPick={onPickSelector} />
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Operator</Typography>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <Select
                    value={getField('operator') || 'contains'} variant="outlined" sx={{ fontSize: '0.8rem' }}
                    onChange={(e) => updateInput('operator', e.target.value)}
                  >
                    {ASSERTION_OPERATORS.map((op) => (
                      <MenuItem key={op.value} value={op.value} sx={{ fontSize: '0.8rem' }}>{op.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Expected Text</Typography>
                <TextField
                  size="small" fullWidth value={getField('value') || ''} variant="outlined"
                  onChange={(e) => updateInput('value', e.target.value)}
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Box>
              <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />
              <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
            </>
          );
        default:
          return <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />;
      }

    case 'control':
      if (d.controlType === 'runScript') {
        return <RunScriptFields step={step} d={d} updateInput={updateInput} onFieldChange={onFieldChange} />;
      }
      return <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />;

    case 'verify':
      if (d.verifyType === 'email') {
        return <VerifyEmailFields step={step} d={d} updateInput={updateInput} onFieldChange={onFieldChange} />;
      }
      return <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />;

    case 'ui':
      if (d.uiType === 'hide_by_text') {
        return <HideByTextFields step={step} d={d} updateInput={updateInput} onFieldChange={onFieldChange} onPickSelector={onPickSelector} />;
      }
      return <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />;

    case 'dom':
      if (d.domType === 'observe_mutation') {
        return <ObserveMutationFields step={step} d={d} updateInput={updateInput} onFieldChange={onFieldChange} onPickSelector={onPickSelector} />;
      }
      return <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />;

    case 'cookie':
      if (d.cookieType === 'read') {
        return <CookieReadFields step={step} d={d} updateInput={updateInput} onFieldChange={onFieldChange} />;
      }
      return <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />;

    case 'tab':
      if (d.tabType === 'open_helper') {
        return <TabOpenHelperFields step={step} d={d} updateInput={updateInput} onFieldChange={onFieldChange} />;
      }
      return <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />;

    case 'event':
      if (d.eventType === 'listen_runtime') {
        return <ListenRuntimeFields step={step} d={d} updateInput={updateInput} onFieldChange={onFieldChange} />;
      }
      return <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />;

    case 'auth':
      if (d.authType === 'sso_redirect_wait') {
        return <SsoRedirectWaitFields step={step} d={d} updateInput={updateInput} onFieldChange={onFieldChange} />;
      }
      return <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />;

    case 'network':
      if (d.networkType === 'intercept') {
        return <NetworkInterceptFields step={step} d={d} updateInput={updateInput} onFieldChange={onFieldChange} />;
      }
      if (d.networkType === 'modify') {
        return <NetworkModifyFields step={step} d={d} updateInput={updateInput} onFieldChange={onFieldChange} />;
      }
      return <TimeoutField value={getField('timeout')} onChange={(v) => updateInput('timeout', v)} />;

    default:
      return null;
  }
};

/**
 * Reusable chip input — text field that turns Enter / commas into chips.
 * The component owns the in-progress text so chips don't materialise on
 * every keystroke. Backspace on an empty draft pops the last chip (matches
 * stock MUI Autocomplete chip-input behaviour).
 */
const ChipInput = ({ label, helperText, values, onChange, placeholder }) => {
  const [draft, setDraft] = useState('');
  const arr = Array.isArray(values) ? values : [];
  const commit = (raw) => {
    const parts = String(raw || '')
      .split(',').map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    onChange([...arr, ...parts]);
    setDraft('');
  };
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 0.5, gap: 0.5 }}>
        {arr.map((v, i) => (
          <Chip
            key={i}
            label={v}
            size="small"
            onDelete={() => onChange(arr.filter((_, j) => j !== i))}
            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
          />
        ))}
      </Stack>
      <TextField
        size="small"
        fullWidth
        value={draft}
        placeholder={placeholder}
        onChange={(e) => {
          const v = e.target.value;
          if (v.endsWith(',')) {
            commit(v.slice(0, -1));
          } else {
            setDraft(v);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(draft);
          } else if (e.key === 'Backspace' && !draft && arr.length) {
            onChange(arr.slice(0, -1));
          }
        }}
        onBlur={() => { if (draft) commit(draft); }}
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

/** ui.hide_by_text — declarative replacement for v3 setInterval+innerText loops. */
const HideByTextFields = ({ step, d, updateInput, onFieldChange, onPickSelector }) => (
  <>
    <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
    <SelectorField
      value={d.selector || ''}
      onChange={(v) => updateInput('selector', v)}
      onPick={onPickSelector}
    />
    <ChipInput
      label="Text contains (ALL — AND-semantics)"
      values={d.textIncludes}
      onChange={(v) => updateInput('textIncludes', v)}
      placeholder='e.g. "spin off" — press Enter to add'
      helperText="Element matches only when ALL listed strings appear in its innerText."
    />
    <ChipInput
      label="Text contains any (OR-semantics)"
      values={d.textIncludesAny}
      onChange={(v) => updateInput('textIncludesAny', v)}
      placeholder="Optional — Enter to add"
      helperText="Element matches when ANY of these strings appears."
    />
    <ChipInput
      label="Text excludes (NONE may appear)"
      values={d.textExcludes}
      onChange={(v) => updateInput('textExcludes', v)}
      placeholder="Optional — Enter to add"
      helperText="Skip elements whose innerText contains any of these."
    />
    <ChipInput
      label="Exclude selectors (skip elements matching these)"
      values={d.excludeSelectors}
      onChange={(v) => updateInput('excludeSelectors', v)}
      placeholder="e.g. .article-renderer"
      helperText="CSS selectors. Element is skipped if it matches OR is contained by one."
    />
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Match mode
      </Typography>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <Select
          value={d.matchMode || 'hide'}
          onChange={(e) => updateInput('matchMode', e.target.value)}
          variant="outlined"
          sx={{ fontSize: '0.8rem' }}
        >
          <MenuItem value="hide" sx={{ fontSize: '0.8rem' }}>Hide (display:none)</MenuItem>
          <MenuItem value="remove" sx={{ fontSize: '0.8rem' }}>Remove (.remove())</MenuItem>
        </Select>
      </FormControl>
    </Box>
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={d.observeMutations !== false}
          onChange={(e) => updateInput('observeMutations', e.target.checked)}
        />
      }
      label={<Typography variant="caption">Re-apply on DOM changes (MutationObserver)</Typography>}
      sx={{ mb: 0.5, display: 'block' }}
    />
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={!!d.caseSensitive}
          onChange={(e) => updateInput('caseSensitive', e.target.checked)}
        />
      }
      label={<Typography variant="caption">Case-sensitive matching</Typography>}
      sx={{ mb: 0.5, display: 'block' }}
    />
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={!!d.targetParent}
          onChange={(e) => updateInput('targetParent', e.target.checked)}
        />
      }
      label={<Typography variant="caption">Target parent element instead of match</Typography>}
      sx={{ mb: 1, display: 'block' }}
    />
    <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
  </>
);

/** dom.observe_mutation — install a MutationObserver + reaction step. */
const ObserveMutationFields = ({ step, d, updateInput, onFieldChange, onPickSelector }) => {
  // The reaction sub-step is currently constrained to ui.hide_by_text. We
  // surface its selector + textIncludes as flat fields and rebuild the
  // nested onMutation.step on every change. This is the simplest design
  // that ships — when later step types are added to the reaction whitelist,
  // a sub-type chooser can wrap this.
  const reactionStep = d?.onMutation?.step || null;
  const reactionInput = reactionStep?.inputData || {};

  const updateReaction = (patch) => {
    const nextInput = { ...reactionInput, ...patch };
    onFieldChange({
      ...step,
      inputData: {
        ...d,
        onMutation: {
          type: 'execute_step',
          step: {
            type: 'ui.hide_by_text',
            name: 'Hide elements by text',
            inputData: { uiType: 'hide_by_text', ...nextInput },
          },
        },
      },
    });
  };

  return (
    <>
      <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Observe target (CSS selector — leave blank for documentElement)
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={d.target || ''}
          onChange={(e) => updateInput('target', e.target.value)}
          placeholder="e.g. nav.main, body, #app"
          sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
        />
      </Box>
      <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Switch size="small" checked={d.childList !== false}
              onChange={(e) => updateInput('childList', e.target.checked)} />
          }
          label={<Typography variant="caption">childList</Typography>}
        />
        <FormControlLabel
          control={
            <Switch size="small" checked={d.subtree !== false}
              onChange={(e) => updateInput('subtree', e.target.checked)} />
          }
          label={<Typography variant="caption">subtree</Typography>}
        />
        <FormControlLabel
          control={
            <Switch size="small" checked={!!d.attributes}
              onChange={(e) => updateInput('attributes', e.target.checked)} />
          }
          label={<Typography variant="caption">attributes</Typography>}
        />
        <FormControlLabel
          control={
            <Switch size="small" checked={!!d.once}
              onChange={(e) => updateInput('once', e.target.checked)} />
          }
          label={<Typography variant="caption">once (disconnect after first match)</Typography>}
        />
      </Box>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Debounce (ms — 0 = single requestAnimationFrame tick)
        </Typography>
        <TextField
          size="small"
          type="number"
          value={d.debounceMs ?? 0}
          onChange={(e) => updateInput('debounceMs', parseInt(e.target.value, 10) || 0)}
          sx={{ width: 140 }}
        />
      </Box>
      <Divider sx={{ my: 1.5 }}>
        <Typography variant="caption" color="text.secondary">Reaction (fired on each mutation batch)</Typography>
      </Divider>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontStyle: 'italic' }}>
        Today only ui.hide_by_text reactions are supported (it's idempotent and safe to re-fire).
      </Typography>
      <SelectorField
        value={reactionInput.selector || ''}
        onChange={(v) => updateReaction({ selector: v })}
        onPick={onPickSelector}
      />
      <ChipInput
        label="Text contains (ALL — AND-semantics)"
        values={reactionInput.textIncludes}
        onChange={(v) => updateReaction({ textIncludes: v })}
        placeholder="Press Enter to add"
        helperText="Element matches only when ALL listed strings appear in its innerText."
      />
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Match mode
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={reactionInput.matchMode || 'hide'}
            onChange={(e) => updateReaction({ matchMode: e.target.value })}
            variant="outlined"
            sx={{ fontSize: '0.8rem' }}
          >
            <MenuItem value="hide" sx={{ fontSize: '0.8rem' }}>Hide (display:none)</MenuItem>
            <MenuItem value="remove" sx={{ fontSize: '0.8rem' }}>Remove (.remove())</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
    </>
  );
};

/** cookie.read — read a cookie by name + (optional) domain into a variable. */
const CookieReadFields = ({ step, d, updateInput, onFieldChange }) => (
  <>
    <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Cookie name (case-sensitive)
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.name || ''}
        onChange={(e) => updateInput('name', e.target.value)}
        placeholder="e.g. KAAC, KAAS, CAUTH"
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Domain (leave blank for current tab hostname)
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.domain || ''}
        onChange={(e) => updateInput('domain', e.target.value)}
        placeholder="e.g. .khanacademy.org"
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Store as variable
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.storeAs || ''}
        onChange={(e) => updateInput('storeAs', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
        placeholder="e.g. kaac"
        helperText={d.storeAs ? `Downstream steps reference this as {{${d.storeAs}}}` : 'Letters, digits, and underscore only'}
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        On missing
      </Typography>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select
          value={d.onMissing || 'continue'}
          onChange={(e) => updateInput('onMissing', e.target.value)}
          variant="outlined"
          sx={{ fontSize: '0.8rem' }}
        >
          <MenuItem value="continue" sx={{ fontSize: '0.8rem' }}>Continue (store empty string)</MenuItem>
          <MenuItem value="fail" sx={{ fontSize: '0.8rem' }}>Fail step</MenuItem>
        </Select>
      </FormControl>
    </Box>
    <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
  </>
);

/** tab.open_helper — open a helper tab (chrome.tabs.create / context.newPage). */
const TabOpenHelperFields = ({ step, d, updateInput, onFieldChange }) => (
  <>
    <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Helper tab URL
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.url || ''}
        onChange={(e) => updateInput('url', e.target.value)}
        placeholder="https://partner.example.com/sso/start"
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={d.waitForLoad !== false}
          onChange={(e) => updateInput('waitForLoad', e.target.checked)}
        />
      }
      label={<Typography variant="caption">Wait for tab to finish loading (DOMContentLoaded)</Typography>}
      sx={{ mb: 0.5, display: 'block' }}
    />
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Wait until URL matches (optional — overrides waitForLoad)
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.waitForUrlMatches || ''}
        onChange={(e) => updateInput('waitForUrlMatches', e.target.value)}
        placeholder="/learn/ — substring, or /^https/.../  for regex"
        helperText="Substring by default. Prefix with `/` for regex."
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Auto-close after (ms — 0 = keep open)
      </Typography>
      <TextField
        size="small"
        type="number"
        value={d.closeAfterMs ?? 0}
        onChange={(e) => updateInput('closeAfterMs', parseInt(e.target.value, 10) || 0)}
        sx={{ width: 160 }}
      />
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Store tab ID as variable (optional)
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.storeTabIdAs || ''}
        onChange={(e) => updateInput('storeTabIdAs', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
        placeholder="e.g. helperTabId"
        helperText={d.storeTabIdAs ? `Downstream steps reference this as {{${d.storeTabIdAs}}}` : 'Letters, digits, and underscore only'}
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
    <TimeoutField value={d.timeoutMs} onChange={(v) => updateInput('timeoutMs', v)} />
    <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
  </>
);

/** event.listen_runtime — wait for a chrome.runtime message. */
const ListenRuntimeFields = ({ step, d, updateInput, onFieldChange }) => (
  <>
    <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Event name (matched against message.type)
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.eventName || ''}
        onChange={(e) => updateInput('eventName', e.target.value)}
        placeholder="e.g. courseraSignupResponse"
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Store payload as variable
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.storeAs || ''}
        onChange={(e) => updateInput('storeAs', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
        placeholder="e.g. signupResponse"
        helperText={d.storeAs ? `Downstream steps reference this as {{${d.storeAs}}} (JSON payload)` : 'Letters, digits, and underscore only'}
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        On timeout
      </Typography>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select
          value={d.onTimeout || 'continue'}
          onChange={(e) => updateInput('onTimeout', e.target.value)}
          variant="outlined"
          sx={{ fontSize: '0.8rem' }}
        >
          <MenuItem value="continue" sx={{ fontSize: '0.8rem' }}>Continue (store empty string)</MenuItem>
          <MenuItem value="fail" sx={{ fontSize: '0.8rem' }}>Fail step</MenuItem>
        </Select>
      </FormControl>
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', fontStyle: 'italic' }}>
      Note: backend headless execution has no chrome.runtime channel — this step will succeed with `n/a in headless`.
    </Typography>
    <TimeoutField value={d.timeoutMs} onChange={(v) => updateInput('timeoutMs', v)} />
    <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
  </>
);

/** auth.sso_redirect_wait — wait for a multi-redirect SAML/OAuth chain. */
const SsoRedirectWaitFields = ({ step, d, updateInput, onFieldChange }) => (
  <>
    <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Until URL matches
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.untilUrlMatches || ''}
        onChange={(e) => updateInput('untilUrlMatches', e.target.value)}
        placeholder="e.g. /learn/ — or /^https:\/\/coursera\.org\/home/ for regex"
        helperText="Substring by default. Prefix with `/` for regex."
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
    <Box sx={{ mb: 1.5, display: 'flex', gap: 1 }}>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Max redirects
        </Typography>
        <TextField
          size="small"
          type="number"
          value={d.maxRedirects ?? 8}
          onChange={(e) => updateInput('maxRedirects', parseInt(e.target.value, 10) || 8)}
          sx={{ width: 100 }}
        />
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Debounce after match (ms)
        </Typography>
        <TextField
          size="small"
          type="number"
          value={d.debounceMs ?? 250}
          onChange={(e) => updateInput('debounceMs', parseInt(e.target.value, 10) || 0)}
          sx={{ width: 140 }}
        />
      </Box>
    </Box>
    <TimeoutField value={d.timeoutMs} onChange={(v) => updateInput('timeoutMs', v)} />
    <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
  </>
);

/** network.intercept — observation-only network capture. */
const NetworkInterceptFields = ({ step, d, updateInput, onFieldChange }) => (
  <>
    <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        URL pattern
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.urlPattern || ''}
        onChange={(e) => updateInput('urlPattern', e.target.value)}
        placeholder="api.coursera.org/forum — or /\\/api\\/v\\d+\\// for regex"
        helperText="Substring by default. Prefix with `/` for regex."
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Method
      </Typography>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select
          value={d.method || 'ANY'}
          onChange={(e) => updateInput('method', e.target.value)}
          variant="outlined"
          sx={{ fontSize: '0.8rem' }}
        >
          {['ANY', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
            <MenuItem key={m} value={m} sx={{ fontSize: '0.8rem' }}>{m}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Store captured payload as variable
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.storeAs || ''}
        onChange={(e) => updateInput('storeAs', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
        placeholder="e.g. forumPostBody"
        helperText={d.storeAs ? `Downstream steps reference this as {{${d.storeAs}}}` : 'Letters, digits, and underscore only'}
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
      />
    </Box>
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={d.captureRequest !== false}
          onChange={(e) => updateInput('captureRequest', e.target.checked)}
        />
      }
      label={<Typography variant="caption">Capture request body</Typography>}
      sx={{ mb: 0.5, display: 'block' }}
    />
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={d.captureResponse !== false}
          onChange={(e) => updateInput('captureResponse', e.target.checked)}
        />
      }
      label={<Typography variant="caption">Capture response body (best-effort in MV3)</Typography>}
      sx={{ mb: 0.5, display: 'block' }}
    />
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={d.onlyFirst !== false}
          onChange={(e) => updateInput('onlyFirst', e.target.checked)}
        />
      }
      label={<Typography variant="caption">Resolve on first match (otherwise accumulate to timeout)</Typography>}
      sx={{ mb: 1, display: 'block' }}
    />
    <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', fontStyle: 'italic' }}>
      Observation-only. Body rewriting is NOT supported — MV3 deprecated webRequestBlocking.
    </Typography>
    <TimeoutField value={d.timeoutMs} onChange={(v) => updateInput('timeoutMs', v)} />
    <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
  </>
);

/**
 * network.modify — body-rewriting follow-up to network.intercept.
 *
 * MV3 deprecated webRequestBlocking, so the only way to rewrite a request
 * or response body is to MAIN-world monkey-patch window.fetch / XHR. This
 * editor exposes the rewrite spec for both directions.
 *
 * SECURITY: 'transform' mode evals an author-supplied JS expression in
 * the page's MAIN world. Only ship trusted expressions.
 */
const NetworkModifyFields = ({ step, d, updateInput, onFieldChange }) => {
  const reqSpec = d.rewriteRequestBody || null;
  const resSpec = d.rewriteResponseBody || null;
  const setReqSpec = (next) => updateInput('rewriteRequestBody', next);
  const setResSpec = (next) => updateInput('rewriteResponseBody', next);
  const renderSpecEditor = (spec, setSpec, label) => {
    if (!spec) {
      return (
        <Box sx={{ mb: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSpec({ mode: 'replace', template: '' })}
          >
            Add {label} rewrite
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={{ mb: 1.5, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {label} rewrite
          </Typography>
          <Button size="small" color="error" onClick={() => setSpec(null)}>
            Remove
          </Button>
        </Box>
        <Box sx={{ mb: 0.75 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={spec.mode || 'replace'}
              onChange={(e) => setSpec({ ...spec, mode: e.target.value })}
              variant="outlined"
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value="replace" sx={{ fontSize: '0.8rem' }}>replace (overwrite body)</MenuItem>
              <MenuItem value="merge_json" sx={{ fontSize: '0.8rem' }}>merge_json (shallow JSON merge)</MenuItem>
              <MenuItem value="transform" sx={{ fontSize: '0.8rem' }}>transform (JS expression)</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {(spec.mode === 'replace' || spec.mode === 'merge_json') && (
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={spec.template || ''}
            onChange={(e) => setSpec({ ...spec, template: e.target.value })}
            placeholder={spec.mode === 'replace' ? '{"new":"body"}' : '{"keys":"to merge in"}'}
            helperText={
              spec.mode === 'replace'
                ? 'Body sent verbatim. Supports {{var}} substitution.'
                : 'Shallow-merged over the parsed original. Supports {{var}}.'
            }
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          />
        )}
        {spec.mode === 'transform' && (
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={spec.transformExpression || ''}
            onChange={(e) => setSpec({ ...spec, transformExpression: e.target.value })}
            placeholder='JSON.stringify({wrapped: JSON.parse(body)})'
            helperText='JS expression with `body` (string) in scope. Eval-equivalent — only ship trusted expressions. {{var}} is NOT substituted (security).'
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          />
        )}
      </Box>
    );
  };
  return (
    <>
      <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          URL pattern
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={d.urlPattern || ''}
          onChange={(e) => updateInput('urlPattern', e.target.value)}
          placeholder="api.coursera.org/forum — or /\\/api\\/v\\d+\\// for regex"
          helperText="Substring by default. Prefix with `/` for regex."
          sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
        />
      </Box>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Method
        </Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={d.method || 'ANY'}
            onChange={(e) => updateInput('method', e.target.value)}
            variant="outlined"
            sx={{ fontSize: '0.8rem' }}
          >
            {['ANY', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
              <MenuItem key={m} value={m} sx={{ fontSize: '0.8rem' }}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {renderSpecEditor(reqSpec, setReqSpec, 'Request body')}
      {renderSpecEditor(resSpec, setResSpec, 'Response body')}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Store captured payload as variable
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={d.storeAs || ''}
          onChange={(e) => updateInput('storeAs', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
          placeholder="e.g. forumPostBody"
          helperText={d.storeAs ? `Downstream steps reference this as {{${d.storeAs}}}` : 'Letters, digits, and underscore only'}
          sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
        />
      </Box>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={d.captureRequest !== false}
            onChange={(e) => updateInput('captureRequest', e.target.checked)}
          />
        }
        label={<Typography variant="caption">Capture (rewritten) request body</Typography>}
        sx={{ mb: 0.5, display: 'block' }}
      />
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={d.captureResponse !== false}
            onChange={(e) => updateInput('captureResponse', e.target.checked)}
          />
        }
        label={<Typography variant="caption">Capture (rewritten) response body</Typography>}
        sx={{ mb: 0.5, display: 'block' }}
      />
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={d.onlyFirst !== false}
            onChange={(e) => updateInput('onlyFirst', e.target.checked)}
          />
        }
        label={<Typography variant="caption">Resolve on first match</Typography>}
        sx={{ mb: 1, display: 'block' }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', fontStyle: 'italic' }}>
        MAIN-world fetch/XHR patch installed at document_start. transform mode runs JS in the page — only ship trusted expressions.
      </Typography>
      <TimeoutField value={d.timeoutMs} onChange={(v) => updateInput('timeoutMs', v)} />
      <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
    </>
  );
};

/** Verify Email step — polls /api/email-intercept/poll, follows link or extracts code */
const VerifyEmailFields = ({ step, d, updateInput, onFieldChange }) => (
  <>
    <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Email address
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <TextField
          size="small"
          fullWidth
          value={d.emailAddress || ''}
          onChange={(e) => updateInput('emailAddress', e.target.value)}
          placeholder="{{email}} or user-test-123@mg.automatrix.nucleos.com"
          sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
        />
        <ExtractVariableButton value={d.emailAddress || ''} onChange={(v) => updateInput('emailAddress', v)} label="Email" />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block', fontStyle: 'italic' }}>
        Use Mailgun-routed addresses (*.@mg.automatrix.nucleos.com) so the intercepter can capture the email.
      </Typography>
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        Subject contains (optional filter)
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.subjectContains || ''}
        onChange={(e) => updateInput('subjectContains', e.target.value)}
        placeholder={'e.g. "Finish signing up"'}
      />
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        App key (optional)
      </Typography>
      <TextField
        size="small"
        fullWidth
        value={d.appKey || ''}
        onChange={(e) => updateInput('appKey', e.target.value)}
        placeholder="e.g. khan-academy"
      />
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
        On receive
      </Typography>
      <FormControl size="small" fullWidth>
        <Select value={d.onReceive || 'follow-link'} onChange={(e) => updateInput('onReceive', e.target.value)}>
          <MenuItem value="follow-link">Follow verification link</MenuItem>
          <MenuItem value="extract-link">Extract link → variable</MenuItem>
          <MenuItem value="extract-code">Extract code → variable</MenuItem>
          <MenuItem value="wait">Just wait for delivery</MenuItem>
        </Select>
      </FormControl>
    </Box>
    {(d.onReceive === 'extract-link' || d.onReceive === 'extract-code') && (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Store as variable
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={d.storeAs || ''}
          onChange={(e) => updateInput('storeAs', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
          placeholder={d.onReceive === 'extract-code' ? 'verificationCode' : 'verificationLink'}
          sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          helperText={`Downstream steps can reference this as {{${d.storeAs || (d.onReceive === 'extract-code' ? 'verificationCode' : 'verificationLink')}}}`}
        />
      </Box>
    )}
    <TimeoutField value={d.timeout} onChange={(v) => updateInput('timeout', v)} />
  </>
);

/** Run Script step — picker that shows scripts from the same app */
const RunScriptFields = ({ step, d, updateInput, onFieldChange }) => {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get the current script's appKey from the step's parent context
  // We pass it via the step's inputData or find it from the editor
  const appKey = d.appKey || step.appKey || '';

  useEffect(() => {
    setLoading(true);
    extensionScriptsService
      .getAll()
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data.items || [];
        // Filter to same app only
        const sameApp = appKey
          ? list.filter((s) => s.appKey === appKey && s.id !== step._parentScriptId)
          : list;
        setScripts(sameApp);
      })
      .catch(() => setScripts([]))
      .finally(() => setLoading(false));
  }, [appKey, step._parentScriptId]);

  const selectedScript = scripts.find((s) => s.id === d.scriptId) || null;

  return (
    <>
      <NameField value={step.name} onChange={(v) => onFieldChange({ ...step, name: v })} />
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Script to Run {appKey && <span style={{ color: '#999' }}>(same app: {appKey})</span>}
        </Typography>
        <Autocomplete
          size="small"
          options={scripts}
          loading={loading}
          getOptionLabel={(s) => s.name || s.id}
          value={selectedScript}
          onChange={(_, script) => {
            updateInput('scriptId', script?.id || '');
            updateInput('scriptName', script?.name || '');
            if (script && !step.name) {
              onFieldChange({ ...step, name: `Run: ${script.name}` });
            }
          }}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select a script..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.scriptType || option.type || 'script'}
                </Typography>
              </Box>
            </li>
          )}
        />
      </Box>
      {d.scriptName && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Will execute all steps from "{d.scriptName}" in the same browser context.
        </Typography>
      )}
      <TimeoutField value={d.timeout || 60000} onChange={(v) => updateInput('timeout', v)} />
      <FailureHandlingField value={d.onFailure} onChange={(v) => updateInput('onFailure', v)} />
    </>
  );
};

// ---- Main StepDetail component ----

const StepDetail = ({
  step,
  index,
  totalSteps,
  onUpdate,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onRunStep,
  onPickSelector, // #68: extension-only — activates picker and updates THIS step's selector
}) => {
  // Bind the generic picker activator to THIS step's selector update, so the
  // SelectorField just gets `onPick(callback)` and doesn't care about indexes.
  const boundPicker = onPickSelector
    ? (cb) => onPickSelector((payload) => cb && cb(payload))
    : null;
  const typeKey = getStepTypeKey(step);

  const handleTypeChange = (newTypeKey) => {
    const tmpl = STEP_TEMPLATES[newTypeKey];
    if (!tmpl) return;
    onUpdate({
      ...step,
      type: tmpl.type,
      name: step.name || tmpl.defaultName,
      inputData: { ...tmpl.inputData },
    });
  };

  return (
    <Box sx={{ ml: 5, mb: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
      {/* Step type chooser */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Step Type
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={typeKey}
            onChange={(e) => handleTypeChange(e.target.value)}
            variant="outlined"
            sx={{ fontSize: '0.85rem' }}
          >
            <MenuItem disabled sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'primary.main' }}>
              -- Actions --
            </MenuItem>
            {STEP_TYPE_ITEMS.filter((i) => i.type === 'action').map((i) => (
              <MenuItem key={i.value} value={i.value} sx={{ fontSize: '0.85rem' }}>{i.label}</MenuItem>
            ))}
            <MenuItem disabled sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'warning.main' }}>
              -- Waits --
            </MenuItem>
            {STEP_TYPE_ITEMS.filter((i) => i.type === 'wait').map((i) => (
              <MenuItem key={i.value} value={i.value} sx={{ fontSize: '0.85rem' }}>{i.label}</MenuItem>
            ))}
            <MenuItem disabled sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'success.main' }}>
              -- Assertions --
            </MenuItem>
            {STEP_TYPE_ITEMS.filter((i) => i.type === 'assertion').map((i) => (
              <MenuItem key={i.value} value={i.value} sx={{ fontSize: '0.85rem' }}>{i.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Type-specific fields */}
      <StepTypeFields step={step} onFieldChange={onUpdate} onPickSelector={boundPicker} />

      {/* Action buttons */}
      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Tooltip title="Move step up">
          <span>
            <IconButton size="small" disabled={index === 0} onClick={onMoveUp}>
              <UpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move step down">
          <span>
            <IconButton size="small" disabled={index >= totalSteps - 1} onClick={onMoveDown}>
              <DownIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Run this step only">
          <IconButton size="small" sx={{ color: '#4caf50' }} onClick={onRunStep}>
            <PlayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Duplicate step">
          <IconButton size="small" onClick={onDuplicate}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Delete step">
          <IconButton size="small" sx={{ color: '#f44336' }} onClick={onDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default StepDetail;
